import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import {
  Query,
  RegenerateAssistantResponseInput,
  RegenerateAssistantResponseStepInput,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const REGENERATE_ASSISTANT_RESPONSE = gql`
  query regenerateAssistantResponse(
    $question: String
    $sessionID: ID!
    $tenantID: ID!
    $userID: ID!
    $responseID: ID!
    $step: RegenerateAssistantResponseStepInput
    $chatEditToken: ID
  ) {
    regenerateAssistantResponse(
      request: {
        question: $question
        sessionID: $sessionID
        tenantID: $tenantID
        userID: $userID
        responseID: $responseID
        step: $step
        chatEditToken: $chatEditToken
      }
    ) {
      responseID
    }
  }
`;

export interface RegenerateAssistantResponseWrapperParams {
  promptID: string;
  question?: string;
  step?: RegenerateAssistantResponseStepInput;
}

export function useRegenerateAssistantResponse() {
  const chatEditToken = useOracleSearchStore(
    ({ data }) => data.chat?.editToken,
  );
  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const [regenerateAssistantResponse, resp] = useLazyQuery<
    Query,
    RegenerateAssistantResponseInput
  >(REGENERATE_ASSISTANT_RESPONSE, {
    fetchPolicy: 'network-only',
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to regenerate the response.',
        props: {},
      });
    },
  });

  const regenerateAssistantResponseWrapper = useCallback(
    async ({
      question,
      promptID,
      step,
    }: RegenerateAssistantResponseWrapperParams) => {
      if (!sessionID) {
        return;
      }

      return regenerateAssistantResponse({
        variables: {
          question,
          sessionID,
          tenantID,
          userID,
          responseID: promptID,
          step,
          chatEditToken,
        },
      });
    },
    [sessionID, chatEditToken, tenantID, userID, regenerateAssistantResponse],
  );

  return [
    regenerateAssistantResponseWrapper,
    {
      responseID: resp.data?.regenerateAssistantResponse?.responseID,
      isLoading: resp.loading,
    },
  ] as const;
}
